import { useState, type ReactNode } from 'react';
import { leave, signIn } from '../auth';
import { enableNotifications, howToAllow, isIos, promptInstall } from '../notifications';
import { permissionNow } from '../gate';
import { useInstallStatus } from '../hooks';
import { APP_NAME, DAILY_NAME } from '../../../shared/constants';
import {
  IconAddToHome,
  IconAperture,
  IconCheck,
  IconClose,
  IconDots,
  IconShare,
  IconViewMore,
  Spinner,
} from '../ui';

export function SignIn() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSignIn = async () => {
    setBusy(true);
    setError(null);
    try {
      await signIn();
    } catch (err) {
      const code = (err as { code?: string }).code ?? '';
      // A closed popup is the user changing their mind, not a failure.
      if (!code.includes('popup-closed') && !code.includes('cancelled')) {
        setError('That did not go through. Try again?');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="app">
      <div className="grow col center" style={{ gap: 40, padding: '0 32px' }}>
        <div style={{ position: 'relative', width: 200, height: 190 }}>
          <span
            style={{
              position: 'absolute',
              left: 4,
              top: 28,
              width: 140,
              height: 140,
              borderRadius: 'var(--r-lg)',
              background: 'var(--surface-2)',
              transform: 'rotate(-9deg)',
            }}
          />
          <span
            style={{
              position: 'absolute',
              left: 50,
              top: 20,
              width: 140,
              height: 140,
              borderRadius: 'var(--r-lg)',
              background: 'var(--well)',
              transform: 'rotate(7deg)',
            }}
          />
          <span
            className="center"
            style={{
              position: 'absolute',
              left: 28,
              top: 25,
              width: 140,
              height: 140,
              borderRadius: 'var(--r-lg)',
              background: 'var(--accent)',
              color: 'var(--accent-glyph)',
            }}
          >
            <IconAperture size={62} />
          </span>
        </div>

        <div className="col center" style={{ gap: 12 }}>
          <h1 style={{ margin: 0, fontSize: 42, fontWeight: 800, letterSpacing: '-1.2px' }}>
            {APP_NAME}
          </h1>
          <p
            className="muted"
            style={{ margin: 0, textAlign: 'center', fontSize: 15, lineHeight: 1.5, maxWidth: 280 }}
          >
            A private feed for the eleven of us. The chat is ours to keep;
            the {DAILY_NAME} is gone tomorrow.
          </p>
        </div>
      </div>

      <div className="col" style={{ gap: 16, padding: '0 24px 44px' }}>
        {error && (
          <span className="small" style={{ color: 'var(--danger)', textAlign: 'center' }}>
            {error}
          </span>
        )}
        <button className="btn btn--block" onClick={onSignIn} disabled={busy}>
          {busy ? <Spinner /> : 'Continue with Google'}
        </button>
        <p className="tiny dim" style={{ margin: 0, textAlign: 'center', lineHeight: 1.5 }}>
          Family only. Carter adds accounts by hand.
        </p>
      </div>
    </div>
  );
}

export function NotAllowed({ email }: { email: string }) {
  return (
    <div className="app">
      <div className="grow col center" style={{ gap: 26, padding: '0 34px' }}>
        <span
          className="center"
          style={{ width: 72, height: 72, borderRadius: 22, background: 'var(--surface)' }}
        >
          <IconClose size={30} />
        </span>
        <div className="col center" style={{ gap: 12 }}>
          <h1 style={{ margin: 0, fontSize: 27, fontWeight: 800, textAlign: 'center' }}>
            This app is private
          </h1>
          <p
            className="muted"
            style={{ margin: 0, textAlign: 'center', fontSize: 15, lineHeight: 1.5 }}
          >
            You signed in fine, but this account is not on the family list, so
            there is nothing here to show you.
          </p>
        </div>
        <div className="card row" style={{ padding: 14, width: '100%', gap: 12 }}>
          <span className="grow col small truncate">{email}</span>
          <span className="tiny dim">not allowlisted</span>
        </div>
      </div>
      <div className="col" style={{ gap: 14, padding: '0 24px 44px' }}>
        <button className="btn btn--block" onClick={() => void leave()}>
          Sign out
        </button>
      </div>
    </div>
  );
}

interface InstallStepSpec {
  id: string;
  icon: ReactNode;
  text: ReactNode;
}

/**
 * One step of the install walkthrough. The icon is the point: people are
 * hunting for a small grey glyph in a toolbar, and a picture of it finds
 * faster than a name does, especially when Apple renames the menu.
 */
function InstallStep({ n, icon, text }: { n: number; icon: ReactNode; text: ReactNode }) {
  return (
    <li className="row" style={{ gap: 12, alignItems: 'center' }}>
      <span
        className="center"
        style={{
          width: 22,
          height: 22,
          flexShrink: 0,
          borderRadius: 'var(--r-pill)',
          background: 'var(--well)',
          color: 'var(--text-2)',
          fontSize: 11,
          fontWeight: 700,
        }}
      >
        {n}
      </span>
      <span
        className="center"
        style={{
          width: 34,
          height: 34,
          flexShrink: 0,
          borderRadius: 'var(--r-sm)',
          background: 'var(--well)',
          border: '1px solid var(--line)',
          color: 'var(--accent)',
        }}
      >
        {icon}
      </span>
      <span className="small grow" style={{ color: 'var(--text-2)', lineHeight: 1.5 }}>
        {text}
      </span>
    </li>
  );
}

const IOS_STEPS: InstallStepSpec[] = [
  {
    id: 'menu',
    icon: <IconDots size={18} />,
    text: (
      <>
        Tap the <span className="bold">menu</span> button in the bar at the bottom of
        Safari.
      </>
    ),
  },
  {
    id: 'share',
    icon: <IconShare size={18} />,
    text: (
      <>
        Tap <span className="bold">Share</span>.
      </>
    ),
  },
  {
    id: 'more',
    icon: <IconViewMore size={18} />,
    text: (
      <>
        Tap <span className="bold">View More</span> — the round arrow near the bottom —
        then scroll the list down.
      </>
    ),
  },
  {
    id: 'add',
    icon: <IconAddToHome size={18} />,
    text: (
      <>
        Choose <span className="bold">Add to Home Screen</span>, then <span className="bold">Add</span>.
      </>
    ),
  },
  {
    id: 'open',
    // The app's own mark, because this step is about recognising it.
    icon: <IconAperture size={18} />,
    text: <>Open {APP_NAME} from the new icon, not from Safari.</>,
  },
];

const OTHER_STEPS: InstallStepSpec[] = [
  {
    id: 'menu',
    icon: <IconDots size={18} />,
    text: <>Open the browser menu.</>,
  },
  {
    id: 'install',
    icon: <IconAddToHome size={18} />,
    text: (
      <>
        Choose <span className="bold">Install app</span>.
      </>
    ),
  },
];

/**
 * iOS only delivers web push to an installed PWA, and asking for permission
 * from Safari fails in a way the user cannot undo. So this screen appears
 * before any permission prompt, and only offers the button when it can work.
 */
export function InstallGate({
  uid,
  onDone,
  refused = false,
}: {
  uid: string;
  onDone: () => void;
  /**
   * Sent here because the phone just said no. The permission can still read
   * as allowed -- iOS refuses the push sign-up when Givi Den is switched off
   * in its Settings -- so this says the settings are the way, whatever it reads.
   */
  refused?: boolean;
}) {
  const [busy, setBusy] = useState(false);
  const [installing, setInstalling] = useState(false);
  const { state, canPrompt } = useInstallStatus();
  const ios = isIos();
  const canAsk = state === 'installed' || !ios;

  const onInstall = async () => {
    setInstalling(true);
    try {
      await promptInstall();
    } finally {
      setInstalling(false);
    }
  };

  const onEnable = async () => {
    setBusy(true);
    try {
      await enableNotifications(uid);
    } finally {
      setBusy(false);
      onDone();
    }
  };

  // Turned down before: the phone has to be told in its settings instead.
  const blocked = refused || permissionNow() === 'denied';
  const [stillBlocked, setStillBlocked] = useState(false);
  const onCheckAgain = async () => {
    setBusy(true);
    try {
      if ((await enableNotifications(uid)) === 'on') onDone();
      else setStillBlocked(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="app">
      <div className="header col" style={{ alignItems: 'flex-start', gap: 8 }}>
        <h1 className="title">
          {state === 'installed' ? 'One quick thing' : 'Two quick things'}
        </h1>
        <p className="muted small" style={{ margin: 0, lineHeight: 1.5 }}>
          The {DAILY_NAME} window is only a couple of minutes long, so notifications
          are most of the point.
        </p>
      </div>

      <div className="scroll pad col" style={{ gap: 14 }}>
        <div className="card col" style={{ padding: 18, gap: 14 }}>
          <div className="row">
            <span
              className="center"
              style={{
                width: 30,
                height: 30,
                borderRadius: 'var(--r-pill)',
                background: state === 'installed' ? 'var(--saved)' : 'var(--accent)',
                color: 'var(--accent-ink)',
                fontWeight: 700,
                fontSize: 14,
              }}
            >
              1
            </span>
            <span className="grow bold" style={{ fontSize: 17 }}>
              Add to Home Screen
            </span>
            {state === 'installed' && (
              <span style={{ color: 'var(--saved)' }}>
                <IconCheck size={20} />
              </span>
            )}
          </div>
          {state === 'installed' ? (
            <span className="small" style={{ color: 'var(--saved)' }}>
              Done — you are running the installed app.
            </span>
          ) : (
            <ol className="col" style={{ margin: 0, padding: 0, gap: 10, listStyle: 'none' }}>
              {(ios ? IOS_STEPS : OTHER_STEPS).map((step, i) => (
                <InstallStep key={step.id} n={i + 1} icon={step.icon} text={step.text} />
              ))}
            </ol>
          )}
          {/* Only Chrome and Edge ever hand us a prompt, and only before it is
              used, so this button appears exactly when it can do something. */}
          {state !== 'installed' && canPrompt && (
            <button className="btn btn--block" onClick={onInstall} disabled={installing}>
              {installing ? <Spinner /> : `Install ${APP_NAME}`}
            </button>
          )}
        </div>

        <div className="card col" style={{ padding: 18, gap: 14 }}>
          <div className="row">
            <span
              className="center"
              style={{
                width: 30,
                height: 30,
                borderRadius: 'var(--r-pill)',
                background: 'var(--accent)',
                color: 'var(--accent-ink)',
                fontWeight: 700,
                fontSize: 14,
              }}
            >
              2
            </span>
            <span className="grow bold" style={{ fontSize: 17 }}>
              Turn on notifications
            </span>
          </div>
          {blocked ? (
            <>
              {/*
                * Turned down once, the phone will not ask again however often
                * this does, so the way back is the phone's own settings.
                */}
              <p className="small" style={{ margin: 0, color: 'var(--text-2)', lineHeight: 1.5 }}>
                Notifications are turned off for {APP_NAME}. To turn them on:{' '}
                {howToAllow(ios, state === 'installed')}
              </p>
              {stillBlocked && (
                <p className="small" style={{ margin: 0, color: 'var(--warn)' }}>
                  Still off. Turn them on in settings, then come back and check again.
                </p>
              )}
              <button className="btn btn--block" onClick={onCheckAgain} disabled={busy}>
                {busy ? <Spinner /> : 'I turned them on, check again'}
              </button>
            </>
          ) : (
            <>
              <p className="small" style={{ margin: 0, color: 'var(--text-2)', lineHeight: 1.5 }}>
                {canAsk
                  ? `One buzz a day for the ${DAILY_NAME}, plus messages.`
                  : `Available once ${APP_NAME} is on your Home Screen.`}
              </p>
              <button className="btn btn--block" onClick={onEnable} disabled={busy || !canAsk}>
                {busy ? <Spinner /> : 'Allow notifications'}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="pad" style={{ paddingBottom: 28 }}>
        <button className="btn btn--ghost btn--block" onClick={onDone}>
          Skip for now
        </button>
      </div>
    </div>
  );
}
